//order-form.tsx

import {
  MenuItem,
  Box,
  Divider,
  Button,
  IconButton,
  InputAdornment,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import React, { Fragment, useEffect, useState, useContext } from "react";
import type { JSX } from "react";
import {
  Formik,
  Form,
  FieldProps,
  FastField,
  FieldArray,
  Field,
  FormikHelpers,
  useFormikContext,
} from "formik";
import * as yup from "yup";
import { AppTextField } from "../components/text-field";
import { validationMessages } from "../utils/validation/messages";
import { Customer } from "../models/customer";
import { DatePicker } from "@mui/x-date-pickers";
import { DateTime } from "luxon";
import { ProductTypes } from "../services/api/products";
import DeleteIcon from "@mui/icons-material/Delete";
import { Product } from "../models/product";
import { FormPrompt } from "./form-prompt";
import { UserContext } from "./app-routes";
import { getAllInfoByISO } from "iso-country-currency";
import { formatCurrency } from "../pages/currency";
import { v4 } from "uuid";
import { UpgradePlanDialog } from "./upgrade-plan-dialog";
import { unitSystemLabel } from "../pages/crops-page";
import { Grower } from "../models/grower";

export class SeedLeafError extends Error {
  constructor(
    public message: string,
    public data?: { [k: string]: string },
  ) {
    super(message);
  }
}

export enum OrderRecurType {
  None = "none",
  Weekly = "weekly",
  BiWeekly = "bi-weekly",
  TriWeekly = "tri-weekly",
  QuadWeekly = "quad-weekly",
  FiveWeekly = "five-weekly",
  SixWeekly = "six-weekly",
  SevenWeekly = "seven-weekly",
  EightWeekly = "eight-weekly",
  NineWeekly = "nine-weekly",
  TenWeekly = "ten-weekly",
}

export interface OrderFormValues {
  customerId: string;
  dateType: "harvestDate" | "startDate";
  date: DateTime | null;
  recur?: {
    id: string;
    type: OrderRecurType;
    endDate: DateTime | null;
  };
  orderProducts: {
    id: string;
    productId: string;
    productSizeId: string;
    quantity: string;
    price: string;
  }[];
  notes: string;
  deliveryDateOffset: string;
}

const validationSchema = yup.object({
  customerId: yup.string().required(validationMessages.Required),
  date: yup
    .date()
    .required(validationMessages.Required)
    .typeError(validationMessages.Date),
  orderRecurId: yup.string(),
  recur: yup.object().when("date", (date: Date) => {
    return yup.object({
      id: yup.string(),
      type: yup.string(),
      endDate: yup.date().when("type", {
        is: (val: OrderRecurType) => {
          return val !== OrderRecurType.None;
        },
        then: yup
          .date()
          .nullable()
          .typeError(validationMessages.Date)
          .min(
            DateTime.fromJSDate(date).isValid
              ? date
              : DateTime.now().startOf("day").toJSDate(),
            "Must be after date",
          )
          .max(
            DateTime.now().startOf("day").plus({
              year: 2,
            }),
            "Must end no more than 2 years from today",
          )
          .required(validationMessages.Required),
        otherwise: yup.date().nullable(),
      }),
    });
  }),
  orderProducts: yup.array().of(
    yup.object().shape({
      productId: yup.string().required(validationMessages.Required),
      productSizeId: yup.string().required(validationMessages.Required),
      quantity: yup
        .number()
        .typeError(validationMessages.Number)
        .required(validationMessages.Required)
        .integer(validationMessages.Integer)
        .positive(validationMessages.Positive),
      price: yup.string().required(validationMessages.Required),
    }),
  ),
  notes: yup.string().max(5000),
  deliveryDateOffset: yup.number(),
});

function useMounted() {
  const [isMounted, setIsMounted] = useState(false);
  React.useEffect(() => {
    setIsMounted(true);
  }, []);
  return isMounted;
}

const OrderProductsSizeField = (props: {
  name: string;
  productId?: number;
  products: Product[];
  grower: Grower;
}) => {
  const { setFieldValue } = useFormikContext<OrderFormValues>();
  const [first, setFirst] = useState(true);
  const { name, productId, products } = props;

  React.useEffect(() => {
    if (first) {
      setFirst(false);
      return;
    }
    setFieldValue(name, "");
  }, [productId]);

  return (
    <>
      <Field name={name}>
        {(fieldProps: FieldProps) => (
          <AppTextField
            {...fieldProps}
            select
            label="Size"
            sx={{ backgroundColor: "#fff" }}
          >
            {productId ? (
              products
                .find((p) => p.id === productId)
                ?.productSizes.filter((size) => size.id != null)
                .map((size) => (
                  <MenuItem key={size.id} value={size.id!.toString()}>
                    {size.type === ProductTypes.Cut
                      ? `${size.size?.name} (${size.weight} ${unitSystemLabel(props.grower)})`
                      : "Live Tray"}
                  </MenuItem>
                ))
            ) : (
              <MenuItem key="none" value="none">
                Select a product
              </MenuItem>
            )}
          </AppTextField>
        )}
      </Field>
    </>
  );
};

const OrderProductsPriceField = (props: {
  name: string;
  productId?: string;
  productSizeId?: string;
  products: Product[];
}) => {
  const { setFieldValue } = useFormikContext<OrderFormValues>();
  const { grower } = useContext(UserContext);
  const isMounted = useMounted();
  const { productId, productSizeId, products, name } = props;

  if (!grower) {
    throw new SeedLeafError("Invalid UserContext");
  }

  // clear the price when the product changes except on the first render
  React.useEffect(() => {
    if (!isMounted) {
      return;
    }
    setFieldValue(name, "");
  }, [productId]);

  // clear the price when the product or size is not set
  // default the price when the size is changed
  React.useEffect(() => {
    if (!isMounted) {
      return;
    }
    if (!productId || !productSizeId) {
      setFieldValue(name, "");
      return;
    }

    let price: number | undefined;

    // Restore the price set with the order when the same
    // product and size are selected
    // if (productSizeId === initialValues.orderProducts[props.i]?.productSizeId) {
    //   price = toCurrency(
    //     initialValues.orderProducts[props.i].price,
    //     grower.CountryISO
    //   ).intValue;
    // } else {
    //   // Fill in with the default product size price
    //   price = props.products
    //     .find((p) => p.id === parseInt(productId))
    //     ?.productSizes.find((pS) => pS.id === parseInt(productSizeId))?.price;
    // }

    // Fill in with the default product size price
    price = products
      .find((p) => p.id === parseInt(productId))
      ?.productSizes.find((pS) => pS.id === parseInt(productSizeId))?.price;

    if (price === undefined) {
      throw new SeedLeafError("Can't find product or product size", {
        productId,
        productSizeId,
      });
    }

    setFieldValue(
      name,
      formatCurrency(price, grower.CountryISO, {
        symbol: "",
        separator: "",
      }),
    );
  }, [productSizeId]);

  return (
    <>
      <Field
        component={AppTextField}
        name={name}
        label="Price"
        sx={{ backgroundColor: "#fff" }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              {getAllInfoByISO(grower.CountryISO).symbol}
            </InputAdornment>
          ),
        }}
      />
    </>
  );
};

const EndDateField = (props: { recurType?: OrderRecurType }) => {
  const { values, setFieldValue } = useFormikContext<OrderFormValues>();
  const [hasChanged, setHasChanged] = useState(false);
  const fieldName = "recur.endDate";

  // - clear the end date when a reccurance is removed
  // - set the end date to the end of the year when a reccurance is set if there's no value

  // end date might change when reccurance type changes

  useEffect(() => {
    // ignore initial change
    if (!hasChanged) {
      setHasChanged(true);
      return;
    }

    switch (props.recurType) {
      // clear end date when reccurance is disabled
      case OrderRecurType.None:
        setFieldValue(fieldName, null);
        break;
      default:
        // default end date to end of year when recurrance
        // is enabled and end date hasn't been set
        if (values.recur?.endDate) {
          break;
        }
        setFieldValue(
          fieldName,
          DateTime.fromObject({
            day: 31,
            month: 12,
            year: DateTime.now().year,
          }),
        );
    }
  }, [props.recurType, setFieldValue]);

  return (
    <Field name={fieldName}>
      {(fieldProps: FieldProps) => (
        <DatePicker
          value={fieldProps.field.value}
          onChange={(date) => {
            setFieldValue(fieldName, date);
          }}
          minDate={DateTime.now()}
          label="End Date"
          format="yyyy/LL/dd"
          disabled={values.recur?.type === OrderRecurType.None}
          slotProps={{
            textField: {
              fullWidth: true,
              variant: "outlined",
              sx: { mt: "4px" }, // ← NICE CLEAN ALIGNMENT FIX
              helperText: fieldProps.meta.touched && fieldProps.meta.error,
              error: !!fieldProps.meta.error && fieldProps.meta.touched,
              inputProps: {
                placeholder: "YYYY/MM/DD",
              },
              name: fieldProps.field.name,
            },
            inputAdornment: {
              position: "start",
            },
          }}
        />
      )}
    </Field>
  );
};

export default function OrderForm(props: {
  children?: React.ReactNode;
  formId?: string;
  initialValues: OrderFormValues;
  onSubmit: (
    values: OrderFormValues,
    formikHelpers: FormikHelpers<OrderFormValues>,
  ) => void;
  customers: Customer[];
  products: Product[];
  editing?: boolean;
  saved: boolean;
}) {
  const { grower } = useContext(UserContext);
  const [isReadyToPrompt, setIsReadyToPrompt] = useState(false);

  useEffect(() => {
    setIsReadyToPrompt(false);
    const timeout = setTimeout(() => {
      setIsReadyToPrompt(true);
    }, 0);

    return () => clearTimeout(timeout);
  }, [props.initialValues]);

  const [openUpgradePlanDialog, setOpenUpgradePlanDialog] = useState(false);

  if (!grower) {
    throw new Error("Invalid context");
  }

  const formId = props.formId ?? "order-form";

  const handleClickRepeats = () => {
    if (grower.Plan === "hobby") {
      setOpenUpgradePlanDialog(true);
    }
  };

  const onClickCancelUpgradePlan = () => {
    setOpenUpgradePlanDialog(false);
  };

  return (
    <Formik
      enableReinitialize
      initialValues={props.initialValues}
      validationSchema={validationSchema}
      onSubmit={async (values, helpers) => {
        await props.onSubmit(values, helpers);
        helpers.resetForm({ values });
      }}
    >
      {(formProps) => {
        return (
          <>
            <FormPrompt
              form={formProps}
              saved={props.saved || !isReadyToPrompt}
            />
            <Form id={formId} noValidate>
              <FastField name="customerId">
                {(fieldProps: FieldProps) => (
                  <AppTextField
                    {...fieldProps}
                    autoFocus
                    select
                    label="Customer"
                  >
                    {props.customers
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .filter((_value, i) =>
                        grower.Plan === "business" ? true : i < 5,
                      )
                      .map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.name}
                        </MenuItem>
                      ))}
                  </AppTextField>
                )}
              </FastField>
              <Box
                mb={2}
                sx={{
                  display: "flex",
                  gap: 2,
                  flexWrap: "nowrap",
                  alignItems: "center",
                }}
              >
                {/* Date Type Selector */}
                {!props.editing && (
                  <Box sx={{ flexBasis: "40%", flexGrow: 1, minWidth: 150 }}>
                    <Field name="dateType">
                      {(fieldProps: FieldProps) => (
                        <AppTextField
                          {...fieldProps}
                          select
                          fullWidth
                          variant="outlined"
                          label="Cycle Trigger"
                          InputLabelProps={{ shrink: true }}
                        >
                          <MenuItem value="harvestDate">
                            Harvest Date (recommended)
                          </MenuItem>
                          <MenuItem value="startDate">
                            Start Date (only works with single-product Orders)
                          </MenuItem>
                        </AppTextField>
                      )}
                    </Field>
                  </Box>
                )}

                {/* Date Picker */}
                <Box
                  sx={{
                    flexBasis: props.editing ? "100%" : "60%",
                    flexGrow: 1,
                    minWidth: 200,
                  }}
                >
                  <Field name="date">
                    {(fieldProps: FieldProps) => (
                      <Box mt={2}>
                        <DatePicker
                          value={fieldProps.field.value}
                          onChange={(date) =>
                            formProps.setFieldValue(fieldProps.field.name, date)
                          }
                          minDate={DateTime.now()}
                          label={
                            formProps.values.dateType === "harvestDate"
                              ? "Harvest Date"
                              : "Start Date"
                          }
                          format="yyyy/LL/dd"
                          disabled={props.editing}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              variant: "outlined",
                              error:
                                !!fieldProps.meta.error &&
                                fieldProps.meta.touched,
                              helperText:
                                fieldProps.meta.touched &&
                                fieldProps.meta.error,
                              InputLabelProps: { shrink: true },
                            },
                          }}
                        />
                      </Box>
                    )}
                  </Field>
                </Box>
              </Box>
              <Box mb={2}>
                <Field name="deliveryDateOffset">
                  {(fieldProps: FieldProps) => (
                    <AppTextField
                      {...fieldProps}
                      select
                      label="Delivery Date"
                      helperText="Number of days after harvest date for delivery"
                    >
                      <MenuItem key="0" value="0">
                        Deliver on Harvest Day
                      </MenuItem>
                      <MenuItem key="1" value="1">
                        Deliver One Day After Harvest Day
                      </MenuItem>
                      <MenuItem key="2" value="2">
                        Deliver Two Days After Harvest Day
                      </MenuItem>
                      <MenuItem key="3" value="3">
                        Deliver Three Days After Harvest Day
                      </MenuItem>
                    </AppTextField>
                  )}
                </Field>
              </Box>
              <Grid container columns={12} spacing={3} mb={1}>
                {/* Repeats */}
                <Grid size={{ xs: 12, md: 8 }}>
                  <Field name="recur.type">
                    {(fieldProps: FieldProps) => (
                      <AppTextField
                        {...fieldProps}
                        select
                        size="medium"
                        label="Repeats"
                        disabled={props.editing || grower.Plan === "hobby"}
                        onClick={handleClickRepeats}
                      >
                        <MenuItem value={OrderRecurType.None}>
                          Does Not Repeat
                        </MenuItem>
                        <MenuItem value={OrderRecurType.Weekly}>
                          Every 1 Week
                        </MenuItem>
                        <MenuItem value={OrderRecurType.BiWeekly}>
                          Every 2 Weeks
                        </MenuItem>
                        <MenuItem value={OrderRecurType.TriWeekly}>
                          Every 3 Weeks
                        </MenuItem>
                        <MenuItem value={OrderRecurType.QuadWeekly}>
                          Every 4 Weeks
                        </MenuItem>
                        <MenuItem value={OrderRecurType.FiveWeekly}>
                          Every 5 Weeks
                        </MenuItem>
                        <MenuItem value={OrderRecurType.SixWeekly}>
                          Every 6 Weeks
                        </MenuItem>
                        <MenuItem value={OrderRecurType.SevenWeekly}>
                          Every 7 Weeks
                        </MenuItem>
                        <MenuItem value={OrderRecurType.EightWeekly}>
                          Every 8 Weeks
                        </MenuItem>
                        <MenuItem value={OrderRecurType.NineWeekly}>
                          Every 9 Weeks
                        </MenuItem>
                        <MenuItem value={OrderRecurType.TenWeekly}>
                          Every 10 Weeks
                        </MenuItem>
                      </AppTextField>
                    )}
                  </Field>
                </Grid>

                {/* End Date */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <Box sx={{ mt: 1.5 }}>
                    <EndDateField recurType={formProps.values.recur?.type} />
                  </Box>
                </Grid>
              </Grid>
              {/* Permanent top divider */}
              <Box mt={4} mb={2}>
                <Divider
                  sx={{
                    borderColor: "rgba(0,0,0,0.5)",
                    borderBottomWidth: "1px",
                  }}
                />
              </Box>
              <FieldArray
                name="orderProducts"
                render={(arrayHelpers) => (
                  <>
                    {formProps.values.orderProducts.map((c, i) => (
                      <Fragment key={c.id}>
                        {/* Divider only for items AFTER the first */}
                        {i > 0 && (
                          <Box mt={2} mb={2}>
                            <Divider
                              sx={{
                                borderColor: "rgba(0,0,0,0.5)",
                                borderBottomWidth: "1px",
                              }}
                            />
                          </Box>
                        )}

                        {/* SHADED PRODUCT BOX */}
                        <Box
                          sx={{
                            backgroundColor: "#f9f9f9",
                            borderRadius: 2,
                            border: "1px solid rgba(0,0,0,0.15)",
                            mt: 1,
                          }}
                        >
                          {/* PRODUCT HEADER STRIP */}
                          <Box
                            sx={{
                              backgroundColor: "#ebebeb",
                              px: 2,
                              py: 1,
                              borderTopLeftRadius: 8,
                              borderTopRightRadius: 8,
                              borderBottom: "1px solid rgba(116, 65, 65, 0.1)",
                            }}
                          >
                            <Typography
                              variant="h6"
                              sx={{ color: "#666", fontWeight: 600 }}
                            >
                              Product {i + 1}
                            </Typography>
                          </Box>

                          {/* PRODUCT FIELDS */}
                          <Box sx={{ p: 2 }}>
                            <Grid
                              container
                              columns={12}
                              spacing={2}
                              alignItems="center"
                              mb={2}
                            >
                              {/* Product */}
                              <Grid size={{ xs: 12, md: 4 }}>
                                <Field name={`orderProducts[${i}].productId`}>
                                  {(fieldProps: FieldProps) => (
                                    <AppTextField
                                      {...fieldProps}
                                      select
                                      sx={{ backgroundColor: "#fff" }}
                                      label="Product"
                                    >
                                      {props.products
                                        .sort((a, b) =>
                                          a.name.localeCompare(b.name),
                                        )
                                        .map((product) => (
                                          <MenuItem
                                            key={product.id}
                                            value={product.id}
                                          >
                                            {product.name}
                                          </MenuItem>
                                        ))}
                                    </AppTextField>
                                  )}
                                </Field>
                              </Grid>

                              {/* Size */}
                              <Grid size={{ xs: 12, md: 3 }}>
                                <OrderProductsSizeField
                                  name={`orderProducts[${i}].productSizeId`}
                                  productId={
                                    c.productId
                                      ? parseInt(c.productId)
                                      : undefined
                                  }
                                  products={props.products}
                                  grower={grower}
                                />
                              </Grid>

                              {/* Quantity */}
                              <Grid size={{ xs: 12, md: 2 }}>
                                <FastField
                                  component={AppTextField}
                                  sx={{ backgroundColor: "#fff" }}
                                  name={`orderProducts[${i}].quantity`}
                                  label="Quantity"
                                />
                              </Grid>

                              {/* Price */}
                              <Grid size={{ xs: 12, md: 2 }}>
                                <OrderProductsPriceField
                                  name={`orderProducts[${i}].price`}
                                  productId={c.productId}
                                  productSizeId={c.productSizeId}
                                  products={props.products}
                                />
                              </Grid>

                              {/* Delete */}
                              <Grid
                                size={{ xs: 12, md: 1 }}
                                sx={{ textAlign: { md: "center" } }}
                              >
                                <IconButton
                                  disabled={
                                    formProps.values.orderProducts.length === 1
                                  }
                                  aria-label="delete"
                                  onClick={() => arrayHelpers.remove(i)}
                                  size="large"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Grid>
                            </Grid>
                          </Box>
                        </Box>
                      </Fragment>
                    ))}

                    <Box mt={5} sx={{ "& > *": { mr: 1 } }}>
                      <Button
                        color="secondary"
                        variant="contained"
                        type="button"
                        onClick={() =>
                          arrayHelpers.push({
                            id: v4(),
                            productId: "",
                            productSizeId: "",
                            quantity: "",
                            price: "",
                          })
                        }
                      >
                        Add Product
                      </Button>
                    </Box>
                  </>
                )}
              />
              <FastField name="notes">
                {(fieldProps: FieldProps) => (
                  <AppTextField
                    {...fieldProps}
                    multiline
                    label="Notes"
                    rows={4}
                  />
                )}
              </FastField>

              {/* Spacer so last fields don't hide behind sticky action bar */}
              <Box sx={{ height: 110 }} />
            </Form>

            {/* ✅ Sticky action bar must be OUTSIDE the <Form> */}
            {props.children}

            <UpgradePlanDialog
              open={openUpgradePlanDialog}
              onClickCancel={onClickCancelUpgradePlan}
            />
          </>
        );
      }}
    </Formik>
  );
}
